import { useState } from "react";
import {
    ArrowUpRight,
    MessageCircle,
    Mail,
    Instagram,
    MapPin,
    Phone,
    Calendar,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    BRAND,
    submitContact,
    openAgendar,
    openWhatsapp,
} from "@/lib/contact";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        whatsapp: "",
        service: "",
        message: "",
    });

    const update = (k) => (e) =>
        setForm((s) => ({
            ...s,
            [k]: e?.target ? e.target.value : e,
        }));

    const onAgendar = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.whatsapp.trim()) {
            toast.error("Completa nombre y WhatsApp para agendar.");
            return;
        }
        // submit as "Solicito agendar..."
        const text = [
            `*Solicito agendar una cita — Estudio Altez Palarin*`,
            ``,
            `*Nombre:* ${form.name || "-"}`,
            `*WhatsApp:* ${form.whatsapp || "-"}`,
            `*Servicio:* ${form.service || "-"}`,
            ``,
            `*Mensaje:*`,
            `${form.message || "-"}`,
        ].join("\n");
        openWhatsapp(text);
        toast.success("Abriendo WhatsApp con tu solicitud. ¡Te respondemos enseguida!");
    };

    const onWhatsapp = () => {
        // general WhatsApp without form data
        openWhatsapp(
            "Hola Estela, vengo del sitio. Quisiera hacer una consulta.",
        );
    };

    return (
        <section
            id="contacto"
            data-testid="contact-section"
            className="relative bg-[var(--uma-forest)] text-[var(--uma-cream)] py-24 md:py-32 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
                style={{
                    background:
                        "radial-gradient(circle, #C5A059, transparent 60%)",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                <div className="text-center max-w-3xl mx-auto reveal mb-16 md:mb-20">
                    <div className="uma-eyebrow text-[var(--uma-gold)]">
                        <span style={{ color: "#C5A059" }}>Hablemos</span>
                    </div>
                    <h2 className="font-serif-display text-[40px] md:text-[64px] lg:text-[76px] leading-[1] mt-6 text-[var(--uma-cream)]">
                        Agenda una consulta
                        <br />
                        <em className="italic font-light text-[var(--uma-gold)]">
                            gratuita.
                        </em>
                    </h2>
                    <p className="mt-7 text-[16px] md:text-[18px] leading-relaxed text-[var(--uma-cream)]/70 font-light">
                        Cuéntanos brevemente tu situación y coordinamos una
                        llamada sin costo ni compromiso. Respondemos en menos
                        de 24 horas hábiles.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Form */}
                    <form
                        data-testid="contact-form"
                        onSubmit={onAgendar}
                        className="reveal lg:col-span-7 bg-[var(--uma-cream)] text-[var(--uma-text)] rounded-2xl p-7 md:p-10 border border-white/10"
                    >
                        <div className="font-serif-display text-[28px] md:text-[34px] text-[var(--uma-forest)] leading-tight">
                            Solicitar consulta
                        </div>
                        <p className="mt-2 text-[14px] text-[var(--uma-text-soft)] font-light">
                            Completa el formulario y te contactamos por el
                            canal que prefieras.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7">
                            <Field label="Nombre completo">
                                <Input
                                    data-testid="contact-name"
                                    value={form.name}
                                    onChange={update("name")}
                                    placeholder="Ej: Carlos Rodríguez"
                                    className="h-12 bg-[var(--uma-cream-white)] border-[var(--uma-cream-border)] focus-visible:ring-[var(--uma-gold)]"
                                />
                            </Field>
                            <Field label="WhatsApp">
                                <Input
                                    data-testid="contact-whatsapp"
                                    value={form.whatsapp}
                                    onChange={update("whatsapp")}
                                    placeholder="+598 9X XXX XXX"
                                    className="h-12 bg-[var(--uma-cream-white)] border-[var(--uma-cream-border)] focus-visible:ring-[var(--uma-gold)]"
                                />
                            </Field>
                        </div>

                        <div className="mt-5">
                            <Field label="¿En qué podemos ayudarte?">
                                <Select
                                    value={form.service}
                                    onValueChange={update("service")}
                                >
                                    <SelectTrigger
                                        data-testid="contact-service"
                                        className="h-12 bg-[var(--uma-cream-white)] border-[var(--uma-cream-border)] focus:ring-[var(--uma-gold)]"
                                    >
                                        <SelectValue placeholder="Selecciona una opción" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="contabilidad">
                                            Contabilidad mensual
                                        </SelectItem>
                                        <SelectItem value="impuestos">
                                            Liquidación de impuestos
                                        </SelectItem>
                                        <SelectItem value="comap">
                                            Proyecto de inversión COMAP
                                        </SelectItem>
                                        <SelectItem value="apertura">
                                            Apertura de empresa
                                        </SelectItem>
                                        <SelectItem value="sueldos">
                                            Liquidación de sueldos
                                        </SelectItem>
                                        <SelectItem value="balances">
                                            Balances y estados financieros
                                        </SelectItem>
                                        <SelectItem value="agro">
                                            Servicios Agro
                                        </SelectItem>
                                        <SelectItem value="general">
                                            Asesoramiento general
                                        </SelectItem>
                                        <SelectItem value="otro">
                                            Otro
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <div className="mt-5">
                            <Field label="Mensaje (opcional)">
                                <Textarea
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={update("message")}
                                    rows={4}
                                    placeholder="Cuéntanos brevemente tu situación..."
                                    className="bg-[var(--uma-cream-white)] border-[var(--uma-cream-border)] focus-visible:ring-[var(--uma-gold)]"
                                />
                            </Field>
                        </div>

                        <div className="mt-7 flex flex-col sm:flex-row gap-3">
                            <button
                                type="submit"
                                data-testid="contact-submit-agendar"
                                className="group flex-1 inline-flex items-center justify-center gap-2 bg-[var(--uma-forest)] text-[var(--uma-cream)] hover:bg-[var(--uma-gold-cta)] hover:text-[var(--uma-forest)] px-7 py-4 rounded-full font-medium transition-all hover:-translate-y-0.5"
                            >
                                <Calendar className="h-4 w-4" />
                                Agendar una cita
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <button
                                type="button"
                                onClick={onWhatsapp}
                                data-testid="contact-submit-whatsapp"
                                className="inline-flex items-center justify-center gap-2 border border-[var(--uma-forest)]/20 text-[var(--uma-forest)] hover:bg-[var(--uma-forest)] hover:text-[var(--uma-cream)] px-7 py-4 rounded-full font-medium transition-all"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Enviar un WhatsApp
                            </button>
                        </div>
                        <p className="mt-3 text-[11px] text-[var(--uma-text-soft)]">
                            Te respondemos en menos de 24 hs hábiles. Tus datos
                            no se comparten con terceros.
                        </p>
                    </form>

                    {/* Direct channels */}
                    <div className="lg:col-span-5 space-y-3">
                        <ChannelCard
                            testid="channel-whatsapp"
                            onClick={() => openWhatsapp()}
                            icon={
                                <MessageCircle
                                    className="h-5 w-5"
                                    strokeWidth={1.5}
                                />
                            }
                            label="WhatsApp"
                            value={BRAND.whatsappDisplay}
                            accent
                        />
                        <ChannelCard
                            testid="channel-phone"
                            onClick={() => {
                                window.location.href = `tel:+${BRAND.whatsappNumber}`;
                            }}
                            icon={<Phone className="h-5 w-5" strokeWidth={1.5} />}
                            label="Teléfono"
                            value={BRAND.phoneDisplay}
                        />
                        <ChannelCard
                            testid="channel-email"
                            onClick={() => {
                                window.location.href = `mailto:${BRAND.email}`;
                            }}
                            icon={<Mail className="h-5 w-5" strokeWidth={1.5} />}
                            label="Email"
                            value={BRAND.email}
                        />
                        <ChannelCard
                            testid="channel-instagram"
                            onClick={() =>
                                window.open(
                                    BRAND.instagramUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                )
                            }
                            icon={
                                <Instagram
                                    className="h-5 w-5"
                                    strokeWidth={1.5}
                                />
                            }
                            label="Instagram"
                            value={BRAND.instagram}
                        />

                        {/* Sedes */}
                        <div className="rounded-2xl border border-[var(--uma-cream)]/10 bg-[var(--uma-forest-2)] p-6 mt-6">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-cream)]/60 mb-5">
                                Nuestras sedes
                            </div>
                            <div className="space-y-5">
                                {BRAND.sedes.map((s, i) => (
                                    <div
                                        key={i}
                                        data-testid={`sede-${s.city.toLowerCase()}`}
                                        className="flex items-start gap-3"
                                    >
                                        <MapPin
                                            className="h-5 w-5 text-[var(--uma-gold)] flex-shrink-0 mt-0.5"
                                            strokeWidth={1.5}
                                        />
                                        <div>
                                            <div className="text-[15px] font-medium text-[var(--uma-cream)]">
                                                Sede {s.city}
                                            </div>
                                            <div className="text-[13px] text-[var(--uma-cream)]/65 font-light mt-0.5">
                                                {s.address}
                                                <br />
                                                {s.region}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-[0.2em] text-[var(--uma-text-soft)] font-medium">
                {label}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function ChannelCard({ icon, label, value, onClick, testid, accent }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`w-full text-left rounded-2xl border p-5 flex items-center gap-4 transition-all hover:-translate-y-0.5 ${
                accent
                    ? "bg-[var(--uma-gold)] text-[var(--uma-forest)] border-[var(--uma-gold)] hover:bg-[var(--uma-cream)]"
                    : "bg-[var(--uma-forest-2)] border-[var(--uma-cream)]/10 text-[var(--uma-cream)] hover:bg-[var(--uma-forest-2)]/80"
            }`}
        >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-[var(--uma-forest)] text-[var(--uma-gold)]">
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <div
                    className={`text-[10px] uppercase tracking-[0.2em] ${
                        accent
                            ? "text-[var(--uma-forest)]/70"
                            : "text-[var(--uma-cream)]/60"
                    }`}
                >
                    {label}
                </div>
                <div className="mt-1 text-[15px] font-medium truncate">
                    {value}
                </div>
            </div>
            <ArrowUpRight className="h-4 w-4 flex-shrink-0 opacity-60" />
        </button>
    );
}
